import type {ReactNode} from 'react';
import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useLatest} from 'ahooks';
import styles from './drag-drop-overlay.module.scss';
import {isAcceptedFile} from '@Components/drag-drop-overlay/drag-drop-overlay.library';
import type {DragDropOverlayProps} from '@Components/drag-drop-overlay/drag-drop-overlay.types';

export function DragDropOverlay({targetElement, disabled = false, className = '', acceptedTypes = [], ...props}: DragDropOverlayProps): ReactNode {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const dragCounterRef = React.useRef(0);
  const onFilesUploadRef = useLatest(props.onFilesUpload);
  const onInvalidFileTypeErrorRef = useLatest(props.onInvalidFileTypeError);
  const acceptedTypesRef = useLatest(acceptedTypes);

  useEffect(() => {
    if (disabled) {
      return;
    }

    const getTargetElement = (): HTMLElement => {
      return targetElement?.current ?? document.body;
    };

    const isEventWithinTarget = (e: DragEvent): boolean => {
      const target = e.target as Node | null;
      if (!target) {
        return false;
      }

      return getTargetElement().contains(target);
    };

    const handleDragEnter = (e: DragEvent): void => {
      e.preventDefault();
      if (!isEventWithinTarget(e)) {
        return;
      }

      dragCounterRef.current = dragCounterRef.current + 1;

      if (e.dataTransfer?.types.includes('Files')) {
        setIsVisible(true);
        setHasBeenVisible(true);
      }
    };

    const handleDragLeave = (e: DragEvent): void => {
      e.preventDefault();
      if (!isEventWithinTarget(e)) {
        return;
      }

      dragCounterRef.current = dragCounterRef.current - 1;

      if (dragCounterRef.current === 0) {
        setIsVisible(false);
      }
    };

    const handleDragOver = (e: DragEvent): void => {
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent): void => {
      e.preventDefault();
      const within = isEventWithinTarget(e);

      dragCounterRef.current = 0;
      setIsVisible(false);

      if (!within) {
        return;
      }

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        getOnFileUpload(files);
      }
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return (): void => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [disabled, targetElement]);

  const getOnFileUpload = (files: File[]): void => {
    if (files.length === 0) {
      return;
    }

    const validFiles: File[] = [];
    const invalidFiles: File[] = [];
    files.forEach((file: File): void => {
      if (acceptedTypesRef.current?.length === 0 || isAcceptedFile(file, acceptedTypesRef.current)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      onInvalidFileTypeErrorRef.current?.();
    } else if (validFiles.length > 0) {
      onFilesUploadRef.current(validFiles);
    }
  };

  const overlayStyle = isVisible ? {display: 'flex'} : undefined;

  const renderHoverContent = (): ReactNode => {
    if (!hasBeenVisible) {
      return null;
    }
    return props.onHoverContent;
  };

  return createPortal(
    <div className={`_full-width _full-height dragdropoverlay ${styles.container} ${className}`} style={overlayStyle}>
      {renderHoverContent()}
    </div>,
    document.body
  );
}
